<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import Input from '../../../../../components-next/input/Input.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '(00) 00000-0000',
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  }
});

const emit = defineEmits(['update:modelValue', 'validation']);

const phoneValue = ref(props.modelValue);
const isValid = ref(!props.required); // Válido por padrão se não for obrigatório
const isTouched = ref(false);
const internalError = ref('');

const applyMask = (value) => {
  // Remove todos os caracteres não-numéricos
  const numbers = value.replace(/\D/g, '');
  
  // Aplica a máscara (00) 00000-0000
  if (numbers.length <= 2) {
    return numbers.length > 0 ? `(${numbers}` : numbers;
  } else if (numbers.length <= 7) {
    return `(${numbers.substring(0, 2)}) ${numbers.substring(2)}`;
  } else if (numbers.length <= 11) {
    return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7)}`;
  } else {
    return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7, 11)}`;
  }
};

const validatePhone = (phone) => {
  if (!phone) {
    // Se for vazio e obrigatório, é inválido
    if (props.required) {
      internalError.value = 'O telefone é obrigatório';
      return false;
    }
    // Se for vazio e não for obrigatório, é válido
    internalError.value = '';
    return true;
  }
  
  // Verifica se após remover todos os não-dígitos temos exatamente 10 ou 11 dígitos
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10 && digits.length !== 11) {
    internalError.value = 'Digite um número de telefone válido no formato (00) 00000-0000';
    return false;
  }
  
  internalError.value = '';
  return true;
};

const handleInput = (e) => {
  const rawValue = e.target.value;
  const maskedValue = applyMask(rawValue);
  phoneValue.value = maskedValue;
  emit('update:modelValue', maskedValue);
  
  // Valida em tempo real independente se o campo foi tocado
  validateAndEmit();
};

const validateAndEmit = () => {
  isValid.value = validatePhone(phoneValue.value);
  emit('validation', isValid.value);
  return isValid.value;
};

const handleBlur = () => {
  // Marca campo como tocado para mostrar erros visuais
  isTouched.value = true;
  validateAndEmit();
};

// Observar mudanças no modelo
watch(() => props.modelValue, (newValue) => {
  if (newValue !== phoneValue.value) {
    phoneValue.value = applyMask(newValue);
    validateAndEmit();
  }
});

// Validar e emitir no montagem do componente
onMounted(() => {
  // Validar o valor inicial sem mostrar erro
  isValid.value = validatePhone(phoneValue.value);
  emit('validation', isValid.value);
});

const errorMessage = computed(() => {
  // Só mostrar erro se o campo já foi tocado
  if (!isTouched.value) return '';
  return props.error || internalError.value;
});
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <Input
      :modelValue="phoneValue"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :error="errorMessage"
      @input="handleInput"
      @blur="handleBlur"
      @update:modelValue="emit('update:modelValue', $event)"
    />
    <!-- Altura fixa para mensagem de erro -->
    <div class="h-5 text-n-ruby-9 text-sm">
      <span v-if="errorMessage && isTouched">{{ errorMessage }}</span>
      <span v-else-if="!isTouched && phoneValue" class="text-n-slate-10 text-xs">
        Formato: (XX) XXXXX-XXXX
      </span>
    </div>
  </div>
</template>